import React, {useState} from 'react';
import * as Yup from 'yup';
import {Row, Col} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {Input, Form, MainLoader, MaskedInput} from '@components';
import {GreenButton, StyledH3, LabelField, WhiteButton} from '@styled';
import {PHONE_DIAL_CODE} from '@app/utils/constants';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {sendInviteError, sendInviteSuccess} from './redux/index';
import {sagaActions} from './redux/saga';

const StyledCard = styled.div`
  max-width: 700px;
  width: 100%;
`;

const CorporateInvite = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector((state) => state.invites.loading);
  const resetForm = useSelector((state) => state.invites.resetForm);
  const apiError = useSelector((state) => state.invites.error);
  const [showSuccessCard, toggleSuccessCard] = useState(false);

  React.useEffect(() => {
    if (resetForm) {
      dispatch(sendInviteSuccess(false));
      toggleSuccessCard(true);
    }
  }, [resetForm]);

  React.useEffect(() => {
    return () => {
      dispatch(sendInviteError(null));
    };
  }, []);

  const handleSubmit = (values) => {
    dispatch({
      type: sagaActions.SEND_INVITE,
      payload: {
        values: {...values, executiveExistsAllowed: false},
      },
    });
  };

  const renderSuccessCard = () => {
    return (
      <section className="content">
        <div className="container-fluid">
          <StyledCard className="card mx-auto">
            <div className="card-body">
              <StyledH3 className="mb-1 mt-0">
                {t('owner.corporateInvite.successTitle')}
              </StyledH3>
              <div className="mt-2">
                {t('owner.corporateInvite.successDescription')}
              </div>
              <div className="mt-5">
                <GreenButton
                  onClick={() => {
                    toggleSuccessCard(false);
                  }}
                >
                  {t('owner.corporateInvite.wantsToSendMore')}
                </GreenButton>
                <WhiteButton
                  type="button"
                  className="ml-2"
                  onClick={() => {
                    history.push('/corporates/invites');
                  }}
                >
                  {t('owner.corporateInvite.backBtnLabel')}
                </WhiteButton>
              </div>
            </div>
          </StyledCard>
        </div>
      </section>
    );
  };

  const renderInviteForm = () => {
    return (
      <div>
        <Form
          initialValues={{
            corporateName: '',
            executiveName: '',
            executiveEmail: '',
            executivePhone: '',
          }}
          validationSchema={Yup.object({
            corporateName: Yup.string().required(
              t('owner.corporateInvite.required.corporateName')
            ),
            executiveName: Yup.string().required(
              t('owner.corporateInvite.required.executiveName')
            ),
            executiveEmail: Yup.string()
              .required(t('owner.corporateInvite.required.executiveEmail'))
              .email(t('owner.corporateInvite.required.invalidExecutiveEmail')),
            executivePhone: Yup.string()
              .required(
                t('owner.corporateInvite.required.executivePhoneNumber')
              )
              .test({
                name: 'is-valid-phone',
                message: t(
                  'owner.corporateInvite.required.invalidExecutivePhone'
                ),
                test: (value) => {
                  let valid = value && value.indexOf('_') === -1;
                  if (valid) {
                    valid = Number(value) > 0;
                  }
                  return valid;
                },
              }),
          })}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          <section className="content">
            <div className="container-fluid">
              <StyledCard className="card mx-auto">
                <div className="card-body">
                  <StyledH3 className="mb-1 mt-0">
                    {t('owner.corporateInvite.pageTitle')}
                  </StyledH3>
                  {apiError && typeof apiError === 'string' ? (
                    <Row className="mt-3">
                      <Col>
                        <div className="alert alert-danger">{apiError}</div>
                      </Col>
                    </Row>
                  ) : null}
                  <Row className="mb-2">
                    <Col xs={12} lg={12}>
                      <Input
                        type="text"
                        name="corporateName"
                        id="CorporateName"
                        label={t('owner.corporateInvite.corporateName')}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={12} lg={12}>
                      <Input
                        type="text"
                        name="executiveName"
                        id="ExecutiveName"
                        label={t('owner.corporateInvite.executiveName')}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={12} lg={12}>
                      <Input
                        type="email"
                        name="executiveEmail"
                        id="ExecutiveEmail"
                        label={t('owner.corporateInvite.executiveEmail')}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={12} lg={12}>
                      <Row noGutters>
                        <Col xs={3} lg={2} className="text-right mt-4">
                          <LabelField className="mb-1 mt-1 mr-2 text-nowrap">
                            {PHONE_DIAL_CODE}
                          </LabelField>
                        </Col>
                        <Col xs={9} lg={10}>
                          <MaskedInput
                            mask="999999999"
                            name="executivePhone"
                            id="PhoneNumber"
                            label={t(
                              'owner.corporateInvite.executivePhoneNumber'
                            )}
                            placeholder=""
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col>
                      <GreenButton type="submit" className="px-5">
                        {t('owner.corporateInvite.submitBtnLabel')}
                      </GreenButton>
                      <WhiteButton
                        type="button"
                        className="ml-1"
                        onClick={() => {
                          history.push('/corporates/invites');
                        }}
                      >
                        {t('owner.corporateInvite.cancelBtnLabel')}
                      </WhiteButton>
                    </Col>
                  </Row>
                </div>
              </StyledCard>
            </div>
          </section>
        </Form>
      </div>
    );
  };

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      {showSuccessCard ? renderSuccessCard() : renderInviteForm()}
    </>
  );
};

export default CorporateInvite;
